import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode
} from 'react';
import { Link } from 'react-router-dom';
import { fmtDatumTijd, initialenVan, toonDelta, toonNaam } from '../lib/format';
import { rangVan, type Tier, type VormPotje } from '../lib/prestaties';
import { IcoonBeker, IcoonInfo, IcoonKruis, IcoonPotlood, IcoonSlot, Symbool } from './icons';
import type { Match, Player } from '../lib/types';

/* ==========================================================================
   Bouwstenen van de huisstijl. Bewust geen component-library: het zijn dunne
   wrappers om Tailwind-klassen, zodat de vorm op één plek staat.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Het merk: de naam, met de kleurschifting van het icoon.

   Een cyaan kopie links, een oranje rechts — dezelfde streek als in
   `public/favicon.svg`. Dat doen we met text-shadow en niet met drie lagen
   tekst, want zo blijft het één woord dat je kunt selecteren en voorlezen. Het
   FR-blokje staat er bewust niet naast: in het tabblad is het het icoon, hier
   zou het twee keer hetzelfde zeggen.
   -------------------------------------------------------------------------- */

const SCHIFTING = '-1.5px 0 #00e5ff, 1.5px 0 #ff4500';
const NEON = '#ccff00';

export function Woordmerk({
  toon = 'donker',
  maat = 'md'
}: {
  /** `donker` voor op een donkere ondergrond, `licht` voor op papierkleur. */
  toon?: 'donker' | 'licht';
  maat?: 'sm' | 'md' | 'lg';
}) {
  const tekst = maat === 'lg' ? 'text-[32px]' : maat === 'sm' ? 'text-[18px]' : 'text-[22px]';

  return (
    <span
      className={`kop block leading-none ${tekst} ${toon === 'donker' ? 'text-white' : 'text-krijt-900'}`}
      style={{ textShadow: SCHIFTING }}
    >
      Foos
      <span style={toon === 'donker' ? { color: NEON } : undefined} className={toon === 'licht' ? 'text-veld-700' : ''}>
        rankr
      </span>
    </span>
  );
}

export function Kaart({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={`rounded-kaart border border-krijt-200 bg-white p-4 shadow-[0_1px_2px_rgba(11,11,11,0.04),0_6px_20px_rgba(11,11,11,0.04)] ${className}`}
    >
      {children}
    </section>
  );
}

export function KaartKop({ titel, hint, actie }: { titel: string; hint?: ReactNode; actie?: ReactNode }) {
  return (
    <header className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="t2">{titel}</h2>
      {hint ? <span className="text-xs text-krijt-500">{hint}</span> : null}
      {actie}
    </header>
  );
}

export function PaginaKop({ titel, onder, rechts }: { titel: string; onder?: ReactNode; rechts?: ReactNode }) {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="t1">{titel}</h1>
        {onder ? <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-krijt-600">{onder}</p> : null}
      </div>
      {rechts ? <div className="flex items-center gap-2">{rechts}</div> : null}
    </header>
  );
}

type KnopVariant = 'primair' | 'gewoon' | 'gevaar' | 'stil';

const knopStijl: Record<KnopVariant, string> = {
  primair: 'bg-veld-700 text-white border-veld-700 hover:bg-veld-800 hover:border-veld-800',
  gewoon: 'bg-white text-krijt-900 border-krijt-300 hover:bg-krijt-50',
  gevaar: 'bg-white text-kritiek border-krijt-300 hover:border-kritiek hover:bg-red-50',
  stil: 'bg-transparent text-krijt-700 border-transparent hover:bg-krijt-100'
};

export function Knop({
  variant = 'gewoon',
  klein,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: KnopVariant; klein?: boolean }) {
  return (
    <button
      {...rest}
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border font-medium
        transition-all duration-150 ease-out
        hover:-translate-y-px hover:shadow-[0_2px_8px_rgba(11,11,11,0.10)]
        active:translate-y-0 active:shadow-none active:scale-[0.98]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-veld-400 focus-visible:ring-offset-1
        disabled:cursor-not-allowed disabled:opacity-50
        disabled:hover:translate-y-0 disabled:hover:shadow-none
        motion-reduce:transition-none motion-reduce:hover:translate-y-0
        ${klein ? 'px-2.5 py-1 text-[13px]' : 'px-3.5 py-2 text-sm'}
        ${knopStijl[variant]} ${className}`}
    />
  );
}

export function KnopLink({
  to,
  children,
  variant = 'gewoon'
}: {
  to: string;
  children: ReactNode;
  variant?: KnopVariant;
}) {
  return (
    <Link
      to={to}
      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-3.5 py-2 text-sm font-medium
        transition-all duration-150 ease-out
        hover:-translate-y-px hover:shadow-[0_2px_8px_rgba(11,11,11,0.10)]
        active:translate-y-0 active:shadow-none active:scale-[0.98]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-veld-400 focus-visible:ring-offset-1
        motion-reduce:transition-none motion-reduce:hover:translate-y-0
        ${knopStijl[variant]}`}
    >
      {children}
    </Link>
  );
}

type BadgeToon = 'neutraal' | 'goed' | 'slecht' | 'uit' | 'beheer' | 'goud';

const badgeStijl: Record<BadgeToon, string> = {
  neutraal: 'bg-krijt-100 text-krijt-600 border-krijt-200',
  goed: 'bg-green-50 text-goed-tekst border-green-200',
  slecht: 'bg-red-50 text-red-900 border-red-200',
  uit: 'bg-krijt-100 text-krijt-400 border-krijt-200',
  beheer: 'bg-veld-50 text-veld-800 border-veld-200',
  goud: 'bg-goud-zacht text-goud border-amber-200'
};

export function Badge({ toon = 'neutraal', children }: { toon?: BadgeToon; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${badgeStijl[toon]}`}
    >
      {children}
    </span>
  );
}

/**
 * De rang bij een rating: Brons, Zilver, Goud, Diamant. De kleur zit in het
 * bolletje en niet in de tekst — een reekskleur mag nooit de enige drager van
 * betekenis zijn, en gekleurde tekst leest slechter.
 */
export function RangBadge({
  rating,
  klein = false,
  uitleg = false
}: {
  rating: number;
  klein?: boolean;
  /** Zet er een vraagtekentje bij dat naar de rangenpagina wijst. */
  uitleg?: boolean;
}) {
  const rang = rangVan(rating);

  const badge = (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-krijt-200 bg-krijt-50 font-semibold text-krijt-700 ${
        klein ? 'px-1.5 py-0.5 text-[10.5px]' : 'px-2 py-0.5 text-[11.5px]'
      }`}
      title={`${rang.label} — vanaf ${rang.vanaf} punten`}
    >
      <span
        aria-hidden="true"
        className={`inline-block rounded-full ${klein ? 'h-1.5 w-1.5' : 'h-2 w-2'}`}
        style={{ background: rang.kleur }}
      />
      {rang.label}
    </span>
  );

  if (!uitleg) return badge;

  return (
    <span className="inline-flex items-center gap-1">
      {badge}
      <Link
        to="/rangen"
        title="Hoe werken de rangen?"
        aria-label="Uitleg over de rangen"
        className="inline-flex text-krijt-400 transition-colors hover:text-veld-700"
      >
        <IcoonInfo className="!h-4 !w-4" />
      </Link>
    </span>
  );
}

/** De laatste potjes als W en V, oudste links. */
export function VormReeks({ potjes }: { potjes: VormPotje[] }) {
  if (!potjes.length) return <span className="text-xs text-krijt-400">—</span>;

  return (
    <span className="flex gap-[3px]">
      {potjes.map((p) => (
        <span
          key={p.matchId}
          title={`${fmtDatumTijd(p.playedAt)} · ${p.stand}`}
          className={`inline-flex h-[18px] w-[18px] items-center justify-center rounded-sm text-[10px] font-bold text-white ${
            p.gewonnen ? 'bg-goed' : 'bg-kritiek'
          }`}
        >
          {p.gewonnen ? 'W' : 'V'}
        </span>
      ))}
    </span>
  );
}

/** De trede van een prestatie: IJzer tot Legende. */
export function TierBadge({ tier, klein = false }: { tier: Tier; klein?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold ${
        klein ? 'px-1.5 py-0.5 text-[10.5px]' : 'px-2 py-0.5 text-[11.5px]'
      }`}
      style={{ borderColor: `${tier.kleur}55`, background: `${tier.kleur}14`, color: tier.kleur }}
    >
      <span
        aria-hidden="true"
        className={`inline-block rounded-full ${klein ? 'h-1.5 w-1.5' : 'h-2 w-2'}`}
        style={{ background: tier.kleur }}
      />
      {tier.label}
    </span>
  );
}

/**
 * Bladeren door een lange lijst. Bewust dom: hij weet alleen hoeveel er zijn en
 * op welke bladzijde je staat, en geeft de nieuwe bladzijde terug. Wat er per
 * bladzijde in staat bepaalt de pagina zelf.
 */
export function Paginering({
  totaal,
  perPagina,
  pagina,
  onPagina,
  onPerPagina
}: {
  totaal: number;
  perPagina: number;
  /** Nul-gebaseerd. */
  pagina: number;
  onPagina: (p: number) => void;
  onPerPagina?: (n: number) => void;
}) {
  const paginas = Math.max(1, Math.ceil(totaal / perPagina));
  const huidige = Math.min(pagina, paginas - 1);

  if (totaal <= perPagina && !onPerPagina) return null;

  /* Bij veel bladzijden alleen de buren tonen, met … ertussen. */
  const nummers: (number | '…')[] = [];
  for (let i = 0; i < paginas; i++) {
    const dichtbij = Math.abs(i - huidige) <= 1 || i === 0 || i === paginas - 1;
    if (dichtbij) nummers.push(i);
    else if (nummers[nummers.length - 1] !== '…') nummers.push('…');
  }

  const knop = 'nums h-8 min-w-8 border px-2 text-sm font-semibold transition disabled:opacity-40';

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-krijt-600">
      <span className="nums">
        {totaal ? `${huidige * perPagina + 1}–${Math.min((huidige + 1) * perPagina, totaal)} van ${totaal}` : 'niets te tonen'}
      </span>

      <span className="flex items-center gap-1.5">
        {onPerPagina ? (
          <select
            className="veld mr-2 w-auto py-1 text-xs"
            value={perPagina}
            onChange={(e) => onPerPagina(Number(e.target.value))}
            aria-label="Regels per bladzijde"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n} per bladzijde
              </option>
            ))}
          </select>
        ) : null}

        <button
          className={`${knop} border-krijt-300 hover:bg-krijt-100`}
          onClick={() => onPagina(huidige - 1)}
          disabled={huidige === 0}
          aria-label="Vorige bladzijde"
        >
          ‹
        </button>

        {nummers.map((n, i) =>
          n === '…' ? (
            <span key={`gat-${i}`} className="px-1 text-krijt-400">
              …
            </span>
          ) : (
            <button
              key={n}
              onClick={() => onPagina(n)}
              className={`${knop} ${
                n === huidige ? 'border-krijt-900 bg-krijt-900 text-white' : 'border-krijt-300 hover:bg-krijt-100'
              }`}
            >
              {n + 1}
            </button>
          )
        )}

        <button
          className={`${knop} border-krijt-300 hover:bg-krijt-100`}
          onClick={() => onPagina(huidige + 1)}
          disabled={huidige >= paginas - 1}
          aria-label="Volgende bladzijde"
        >
          ›
        </button>
      </span>
    </div>
  );
}

export function Tegel({ label, waarde, onder }: { label: string; waarde: ReactNode; onder?: ReactNode }) {
  return (
    <div className="rounded-kaart border border-krijt-200 bg-white px-4 py-3.5">
      <div className="text-xs uppercase tracking-wide text-krijt-500">{label}</div>
      <div className="mt-1 text-[28px] font-semibold leading-tight">{waarde}</div>
      {onder ? <div className="mt-1 text-xs text-krijt-600">{onder}</div> : null}
    </div>
  );
}

/* ---------- skeletons ----------

   Tijdens het laden houden we de vorm van het scherm vast in plaats van een
   spinner te tonen: dan springt de opmaak niet zodra de gegevens binnen zijn,
   en zie je meteen waar je terechtkomt. Alleen het eerste laden krijgt een
   skeleton; bij verversen blijven de oude cijfers staan (TanStack Query houdt
   ze vast), want knipperende getallen zijn onrustiger dan even oude getallen.
   ------------------------------------------------------------------------- */

export function Skelet({ className = '' }: { className?: string }) {
  return <span className={`block animate-pulse rounded bg-krijt-200 ${className}`} aria-hidden="true" />;
}

export function SkeletTegels({ aantal = 4 }: { aantal?: number }) {
  return (
    <div className="grid gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: aantal }, (_, i) => (
        <div key={i} className="rounded-kaart border border-krijt-200 bg-white px-4 py-3.5">
          <Skelet className="h-3 w-20" />
          <Skelet className="mt-2.5 h-7 w-16" />
          <Skelet className="mt-2 h-3 w-24" />
        </div>
      ))}
    </div>
  );
}

export function SkeletTabel({ rijen = 6, kolommen = 5 }: { rijen?: number; kolommen?: number }) {
  return (
    <div role="status" aria-label="Bezig met laden">
      <div className="flex gap-3 border-b border-krijt-200 pb-2">
        {Array.from({ length: kolommen }, (_, k) => (
          <Skelet key={k} className={`h-2.5 ${k === 1 ? 'flex-[3]' : 'flex-1'}`} />
        ))}
      </div>
      {Array.from({ length: rijen }, (_, r) => (
        <div key={r} className="flex items-center gap-3 border-b border-krijt-200 py-3 last:border-b-0">
          {Array.from({ length: kolommen }, (_, k) =>
            k === 1 ? (
              <span key={k} className="flex flex-[3] items-center gap-2">
                <Skelet className="h-7 w-7 shrink-0 rounded-full" />
                <Skelet className="h-3 w-24" />
              </span>
            ) : (
              <Skelet key={k} className="h-3 flex-1" />
            )
          )}
        </div>
      ))}
    </div>
  );
}

export function SkeletRegels({ aantal = 6 }: { aantal?: number }) {
  return (
    <div role="status" aria-label="Bezig met laden">
      {Array.from({ length: aantal }, (_, i) => (
        <div key={i} className="flex items-center gap-3 border-b border-krijt-200 py-2.5 last:border-b-0">
          <Skelet className="h-3 w-24 shrink-0" />
          <span className="flex flex-1 items-center gap-2">
            <Skelet className="h-6 w-6 shrink-0 rounded-full" />
            <Skelet className="h-3 w-20" />
          </span>
          <Skelet className="h-5 w-16 shrink-0 rounded-md" />
          <span className="flex flex-1 items-center justify-end gap-2">
            <Skelet className="h-3 w-20" />
            <Skelet className="h-6 w-6 shrink-0 rounded-full" />
          </span>
        </div>
      ))}
    </div>
  );
}

export function SkeletVlak({ hoogte = 'h-56' }: { hoogte?: string }) {
  return <Skelet className={`w-full ${hoogte}`} />;
}

export function Leeg({ tekst, ikoon = 'blad' }: { tekst: string; ikoon?: string }) {
  return (
    <div className="px-5 py-9 text-center text-sm text-krijt-500">
      <Symbool naam={ikoon} className="mx-auto mb-2.5 !h-8 !w-8 text-krijt-300" />
      {tekst}
    </div>
  );
}

export function Foutmelding({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-[13.5px] text-red-900">
      {children}
    </div>
  );
}

/** Nog niet stuk, wel iets om te weten: een grens die in zicht komt. */
export function Waarschuwing({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-[13.5px] text-amber-900">
      {children}
    </div>
  );
}

export function Melding({ children }: { children: ReactNode }) {
  return (
    <div className="mb-3 rounded-md border border-green-200 bg-green-50 px-3 py-2.5 text-[13.5px] text-green-900">
      {children}
    </div>
  );
}

export function Veld({ label, help, children }: { label: string; help?: ReactNode; children: ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1.5 block text-[13px] font-medium text-krijt-600">{label}</span>
      {children}
      {help ? <span className="mt-1 block text-xs text-krijt-500">{help}</span> : null}
    </label>
  );
}

export function Schakelaar<T extends string>({
  opties,
  waarde,
  onKies
}: {
  opties: { waarde: T; label: string }[];
  waarde: T;
  onKies: (waarde: T) => void;
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-krijt-300">
      {opties.map((o, i) => (
        <button
          key={o.waarde}
          type="button"
          onClick={() => onKies(o.waarde)}
          className={`px-3 py-1.5 text-[13px] transition ${i > 0 ? 'border-l border-krijt-200' : ''} ${
            o.waarde === waarde ? 'bg-veld-700 font-semibold text-white' : 'bg-white text-krijt-600 hover:bg-krijt-50'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ---------- spelers ---------- */

export function Avatar({ speler, maat = 'md' }: { speler: Player; maat?: 'sm' | 'md' | 'lg' }) {
  const maten = {
    sm: 'h-6 w-6 text-[11px]',
    md: 'h-8 w-8 text-[13px]',
    lg: 'h-16 w-16 text-2xl'
  }[maat];

  if (speler.photo) {
    return <img src={speler.photo} alt="" className={`${maten} shrink-0 rounded-full border border-black/10 object-cover`} />;
  }
  return (
    <span
      className={`${maten} inline-flex shrink-0 items-center justify-center rounded-full border border-black/10 bg-krijt-100 font-semibold text-krijt-600`}
    >
      {initialenVan(speler)}
    </span>
  );
}

export function SpelerCel({
  speler,
  maat = 'sm',
  link = true,
  metBijnaam = false
}: {
  speler: Player;
  maat?: 'sm' | 'md';
  /* Alleen aan waar er ruimte voor is. In een wedstrijdrij staan al vier
     namen op een regel; daar zou het alleen maar drukker worden. */
  metBijnaam?: boolean;
  /* Uit als de cel zelf al in iets klikbaars zit: een link in een knop levert
     twee bedoelingen op één klik, en je komt dan op het profiel terwijl je
     iemand wilde aanvinken. */
  link?: boolean;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <Avatar speler={speler} maat={maat} />
      {link ? (
        <Link to={`/speler/${speler.id}`} className="truncate hover:underline">
          {toonNaam(speler)}
        </Link>
      ) : (
        <span className="truncate">{toonNaam(speler)}</span>
      )}
      {metBijnaam && speler.bijnaam ? (
        <span className="truncate text-xs text-krijt-500" title="bijnaam">
          &ldquo;{speler.bijnaam}&rdquo;
        </span>
      ) : null}
    </span>
  );
}

export function WedstrijdRegel({
  potje,
  zoekSpeler,
  delta,
  deltaB,
  vast,
  opVerwijder,
  opAanpassen
}: {
  potje: Match;
  zoekSpeler: (id: string) => Player;
  /* Sinds 028 zijn de twee kanten niet meer elkaars tegengestelde: winnen
     levert meer op dan verliezen kost. Dus allebei apart doorgeven. */
  delta?: number;
  deltaB?: number;
  /** Ligt de uitslag vast? Dan staat er een slotje waar de knoppen zaten. */
  vast?: boolean;
  opVerwijder?: () => void;
  opAanpassen?: () => void;
}) {
  const aWint = potje.scoreA > potje.scoreB;

  /*
   * De verschuiving staat bij iedereen die meespeelde, aan beide kanten. In een
   * duo krijgen ploeggenoten hetzelfde: de Elo-berekening rekent met het
   * teamgemiddelde en deelt de uitkomst niet op.
   */
  const kant = (ids: string[], wint: boolean, rechts: boolean, verschil?: number) => (
    <div className={`flex min-w-0 flex-1 items-center gap-2 ${rechts ? 'justify-end text-right' : ''}`}>
      {ids.map((id, i) => {
        const p = zoekSpeler(id);
        return (
          <span key={id} className="flex min-w-0 items-center gap-1.5">
            {i > 0 ? <span className="text-krijt-400">&</span> : null}
            <Avatar speler={p} maat="sm" />
            <Link
              to={`/speler/${id}`}
              className={`truncate hover:underline ${wint ? 'font-semibold' : 'text-krijt-600'}`}
            >
              {toonNaam(p)}
            </Link>
            {verschil !== undefined ? (
              <span
                className={`nums shrink-0 text-[11px] font-semibold ${
                  verschil > 0 ? 'text-goed-tekst' : verschil < 0 ? 'text-kritiek' : 'text-krijt-500'
                }`}
                title="Wat dit potje met de rating deed"
              >
                {toonDelta(verschil)}
              </span>
            ) : null}
          </span>
        );
      })}
    </div>
  );

  return (
    <div className="flex items-center gap-3 border-b border-krijt-200 py-2.5 last:border-b-0">
      <span className="w-[108px] shrink-0 text-xs text-krijt-500">{fmtDatumTijd(potje.playedAt)}</span>
      {/* Wat niet meetelt heeft ook geen verschuiving om te tonen. */}
      {kant(potje.teamA, aWint, false, potje.teltMee === false ? undefined : delta)}
      <span className="nums shrink-0 rounded-md bg-krijt-100 px-2.5 py-0.5 text-base font-semibold">
        {potje.scoreA} – {potje.scoreB}
      </span>
      {kant(
        potje.teamB,
        !aWint,
        true,
        deltaB === undefined || potje.teltMee === false ? undefined : deltaB
      )}
      {potje.tournamentId ? (
        <span className="shrink-0" title="toernooipotje">
          <IcoonBeker className="text-goud" />
        </span>
      ) : null}
      {potje.teltMee === false ? (
        <span
          className="shrink-0 whitespace-nowrap rounded-full border border-krijt-300 bg-krijt-100 px-2 py-0.5 text-[11px] font-semibold text-krijt-600"
          title="Dit potje telt niet mee voor de ranglijst."
        >
          buiten de ranglijst
        </span>
      ) : null}
      {potje.status === 'wacht' ? (
        <span
          className="shrink-0 whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
          title="Telt nog niet mee: de andere kant moet de stand nog bevestigen."
        >
          wacht
        </span>
      ) : null}
      {opAanpassen ? (
        <Knop variant="stil" klein onClick={opAanpassen} title="Stand aanpassen">
          <IcoonPotlood />
        </Knop>
      ) : null}
      {opVerwijder ? (
        <Knop variant="stil" klein onClick={opVerwijder} title="Wedstrijd verwijderen" className="!text-kritiek">
          <IcoonKruis />
        </Knop>
      ) : null}
      {vast ? (
        <span
          className="shrink-0"
          title="Deze uitslag ligt vast: na een week is er niets meer aan te veranderen."
        >
          <IcoonSlot className="text-krijt-400" />
        </span>
      ) : null}
    </div>
  );
}

/* ---------- modaal ---------- */

export function Modaal({
  titel,
  onSluit,
  children,
  voet
}: {
  titel: string;
  onSluit: () => void;
  children: ReactNode;
  voet?: ReactNode;
}) {
  useEffect(() => {
    const opToets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSluit();
    };
    document.addEventListener('keydown', opToets);
    return () => document.removeEventListener('keydown', opToets);
  }, [onSluit]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-5"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onSluit();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titel}
        className="max-h-[86vh] w-full max-w-xl overflow-auto rounded-kaart border border-krijt-200 bg-white p-5 shadow-2xl"
      >
        <h2 className="mb-3 text-[17px] font-semibold">{titel}</h2>
        {children}
        {voet ? <div className="mt-4 flex justify-end gap-2">{voet}</div> : null}
      </div>
    </div>
  );
}

export function Bevestig({
  titel,
  tekst,
  knop,
  onJa,
  onNee
}: {
  titel: string;
  tekst: string;
  knop: string;
  onJa: () => void;
  onNee: () => void;
}) {
  return (
    <Modaal
      titel={titel}
      onSluit={onNee}
      voet={
        <>
          <Knop onClick={onNee}>Annuleren</Knop>
          <Knop variant="gevaar" onClick={onJa}>
            {knop}
          </Knop>
        </>
      }
    >
      <p className="text-sm text-krijt-600">{tekst}</p>
    </Modaal>
  );
}

/* ---------- toasts ---------- */

interface Toast {
  id: number;
  tekst: string;
  slecht: boolean;
}

const ToastContext = createContext<((tekst: string, slecht?: boolean) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const meld = useCallback((tekst: string, slecht = false) => {
    const id = Date.now() + Math.random();
    setToasts((oud) => [...oud, { id, tekst, slecht }]);
    setTimeout(() => setToasts((oud) => oud.filter((t) => t.id !== id)), slecht ? 6000 : 3000);
  }, []);

  const waarde = useMemo(() => meld, [meld]);

  return (
    <ToastContext.Provider value={waarde}>
      {children}
      <div className="fixed bottom-5 right-5 z-60 flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`max-w-xs rounded-md px-3.5 py-2.5 text-[13.5px] text-white shadow-lg ${
              t.slecht ? 'bg-kritiek' : 'bg-krijt-900'
            }`}
          >
            {t.tekst}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const meld = useContext(ToastContext);
  if (!meld) throw new Error('useToast moet binnen een ToastProvider staan.');
  return meld;
}

/* ---------- tabel ---------- */

export function Tabel({ koppen, children }: { koppen: ReactNode[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {koppen.map((k, i) => (
              <th
                key={i}
                className="whitespace-nowrap border-b border-krijt-200 px-2 py-1.5 text-left text-[11.5px] font-semibold uppercase tracking-wide text-krijt-500 last:text-right"
              >
                {k}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Rij({ children }: { children: ReactNode }) {
  return <tr className="border-b border-krijt-200 last:border-b-0 hover:bg-krijt-50">{children}</tr>;
}

export function Cel({
  children,
  nummer,
  rechts,
  className = ''
}: {
  children?: ReactNode;
  nummer?: boolean;
  rechts?: boolean;
  className?: string;
}) {
  return (
    <td
      className={`px-2 py-2.5 align-middle ${nummer ? 'nums text-right' : ''} ${rechts ? 'text-right' : ''} ${className}`}
    >
      {children}
    </td>
  );
}

export function Plek({ nr, gedeeld = false }: { nr: number; gedeeld?: boolean }) {
  const kleur = nr === 1 ? 'text-goud' : nr === 2 ? 'text-krijt-500' : nr === 3 ? 'text-amber-800' : 'text-krijt-400';
  return (
    <span
      className={`nums font-semibold ${nr <= 3 ? kleur : 'text-krijt-400'}`}
      title={gedeeld ? 'Gedeelde plek: even veel punten' : undefined}
    >
      {nr}
    </span>
  );
}
